if (!customElements.get('recently-viewed-drawer')) {
  class RecentlyViewedDrawer extends HTMLElement {
    constructor() {
      super();

      // main-collection-product-grid.liquid renders this inside
      // .product-grid-container, which (like other scroll-reveal sections)
      // picks up a CSS transform for its slide-in animation. A transformed
      // ancestor becomes the containing block for position:fixed
      // descendants, so the tab/toast/panel were being boxed into that
      // section instead of the real viewport - re-parenting straight onto
      // <body> guarantees they're always positioned against the true window.
      document.body.appendChild(this);

      this.tabButton = this.querySelector('.recently-viewed-drawer__tab');
      this.overlay = this.querySelector('.recently-viewed-drawer__overlay');
      this.panel = this.querySelector('.recently-viewed-drawer__panel');
      this.closeButton = this.querySelector('.recently-viewed-drawer__close');
      this.grid = this.querySelector('.recently-viewed-drawer__grid');
      this.loadingEl = this.querySelector('.recently-viewed-drawer__loading');
      this.countEls = this.querySelectorAll('.recently-viewed-drawer__count');
      this.toastEl = this.querySelector('.recently-viewed-drawer__toast');
      this.toastImage = this.querySelector('.recently-viewed-drawer__toast-image');
      this.toastTitle = this.querySelector('.recently-viewed-drawer__toast-title');

      this.handles = [];
      this.loaded = false;

      this.tabButton.addEventListener('click', this.open.bind(this));
      this.closeButton.addEventListener('click', this.close.bind(this));
      this.overlay.addEventListener('click', this.close.bind(this));
      this.grid.addEventListener('click', this.handleCardClick.bind(this));
      this.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      });

      this.init();
      this.maybeShowToast();

      // Bfcache: hitting the browser Back/Forward button can restore this
      // entire page from an in-memory snapshot instead of reloading it -
      // the constructor never re-runs, so without this the drawer keeps
      // showing whatever list it had at the moment the user navigated away,
      // even if they viewed more products since then. pageshow with
      // event.persisted === true is the standard signal that a page came
      // back from that cache rather than a fresh load.
      window.addEventListener('pageshow', async (event) => {
        if (!event.persisted) return;
        this.loaded = false;
        await this.init();
        if (this.hasAttribute('open')) await this.renderCards();
        // maybeShowToast() only ran from the constructor before, so a page
        // restored from bfcache (constructor doesn't re-run) could never
        // show it again - it needs the same re-check on every bfcache
        // restore, same as the list above. It still no-ops correctly if
        // there's no new lastViewedProductHandle to show.
        this.maybeShowToast();
      });
    }

    async init() {
      this.handles = await this.getRecentlyViewedHandles();
      if (!this.handles.length) return;

      this.countEls.forEach((el) => {
        el.textContent = this.handles.length;
      });
      this.removeAttribute('hidden');
    }

    // Shows a transient "Added to Recently Viewed" preview, expanding out of
    // the tab, then collapsing back into it. layout/theme.liquid stamps the
    // handle into localStorage on every PDP page load; we read + immediately
    // clear it here, so this only fires on the PLP load right after a PDP
    // visit - not on every page load.
    async maybeShowToast() {
      const handle = this.getAndClearLastViewedProductHandle();
      if (!handle) return;

      let product;
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return;
        product = await response.json();
      } catch (error) {
        console.error('[recently-viewed-drawer]', error);
        return;
      }

      const image = product.featured_image || (product.images && product.images[0]);
      if (!image) return;

      this.removeAttribute('hidden');
      this.toastImage.src = `${image}${image.includes('?') ? '&' : '?'}width=200`;
      this.toastImage.alt = product.title || '';
      this.toastTitle.textContent = product.title || '';

      this.tabButton.classList.add('recently-viewed-drawer__tab--toast-active');

      requestAnimationFrame(() => {
        this.toastEl.classList.add('is-visible');
      });

      setTimeout(() => {
        this.toastEl.classList.remove('is-visible');
        this.tabButton.classList.remove('recently-viewed-drawer__tab--toast-active');
      }, 2000);
    }

    // card-product.liquid only wraps the title text in an <a> - the image
    // itself isn't a link, so clicking anywhere else on the card did nothing.
    // Delegate: clicking anywhere on a card (that isn't already a link/button)
    // sends the customer to that product's page.
    handleCardClick(event) {
      if (event.target.closest('a, button')) return;

      const item = event.target.closest('.recently-viewed-drawer__item');
      const link = item && item.querySelector('a.full-unstyled-link[href]');
      if (link) window.location.href = link.href;
    }

    getAndClearLastViewedProductHandle() {
      try {
        const handle = localStorage.getItem('lastViewedProductHandle');
        localStorage.removeItem('lastViewedProductHandle');
        return handle;
      } catch (error) {
        return null;
      }
    }

    // Mirrors product-info.js's addRecentlyViewed() - same cookie / API - so
    // the count here always matches the other recently-viewed surfaces on site.
    async getRecentlyViewedHandles() {
      if (window.__st && window.__st.cid) {
        try {
          // cache: 'no-store' - this URL is identical on every call (same
          // cid), and browsers are especially eager to serve fetch() GET
          // responses from cache on back/forward navigation, so without this
          // a bfcache-triggered refetch can silently return the same stale
          // list instead of hitting the server.
          const response = await fetch(
            `https://api.givadiva.co/v2/omnichannel/viewedProducts?user_shopify_id=${window.__st.cid}`,
            { cache: 'no-store' }
          );
          const data = await response.json();
          if (Array.isArray(data.data)) return data.data.slice(-10).reverse();
        } catch (error) {
          console.error('[recently-viewed-drawer]', error);
        }
      }

      if (!window.givaProductLists) return [];
      return window.givaProductLists
        .handles('recentlyViewedObj')
        .filter(Boolean)
        .slice(-10)
        .reverse();
    }

    async open() {
      this.setAttribute('open', '');
      this.tabButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      trapFocus(this, this.panel);

      if (!this.loaded) await this.renderCards();
    }

    close() {
      this.removeAttribute('open');
      this.tabButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.tabButton);
    }

    async renderCards() {
      this.loadingEl.classList.remove('hidden');

      const settled = await Promise.allSettled(
        this.handles.map((handle) =>
          fetch(`/products/${handle}?view=card`)
            .then((response) => response.text())
            .then((markup) => {
              const doc = new DOMParser().parseFromString(markup, 'text/html');
              const card = doc.querySelector('.template-card');
              return card ? card.innerHTML : null;
            })
        )
      );

      const cardsHtml = settled
        .filter((result) => result.status === 'fulfilled' && result.value)
        .map(
          (result, index) => `
            <li class="grid__item recently-viewed-drawer__item" style="--animation-order: ${index + 1};">
              ${result.value}
            </li>
          `
        )
        .join('');

      this.grid.innerHTML = cardsHtml;
      this.loadingEl.classList.add('hidden');
      this.loaded = true;
    }
  }

  customElements.define('recently-viewed-drawer', RecentlyViewedDrawer);
}
